import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Gift, Star, MapPin, CalendarDays, Store, UserRound, Sparkles, Trophy, Share2, Check, Loader2, QrCode, Cake, Bell, Music, ChevronRight, Ticket, Medal } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MemberCard } from "@/pages/MyRewards";

const RED = "#df1721";
const ICONS = { user: UserRound, store: Store, "map-pin": MapPin, calendar: CalendarDays, gift: Gift };
const GENRES = ["Country", "Rock", "Pop", "Hip-Hop", "R&B", "Jazz", "Blues", "Reggae", "EDM", "Latin", "Zydeco", "Gospel", "Acoustic", "Cover Bands", "Karaoke", "Festivals", "Nightlife", "Family Fun", "Food & Drink", "Outdoors", "Water Sports", "Live Music", "Arts & Culture", "Sports"];
const TIER_ICON = { "VIP Explorer": Star, "VIP Insider": Sparkles, "VIP Navigator": MapPin, "Gulf Coast Elite": Crown };

function Confetti({ n = 26 }) {
  const bits = useMemo(() => Array.from({ length: n }, (_, i) => ({
    left: `${(i * 137) % 100}%`, delay: `${(i * 0.73) % 6}s`, dur: `${6 + (i % 5)}s`,
    size: 4 + (i % 4) * 2, white: i % 3 === 0,
  })), [n]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {bits.map((b, i) => (
        <span key={i} className="vip-rise absolute rounded-sm" style={{
          left: b.left, bottom: "-12px", width: b.size, height: b.size,
          background: b.white ? "rgba(255,255,255,0.5)" : RED, opacity: 0.7,
          animationDelay: b.delay, animationDuration: b.dur,
        }} />
      ))}
    </div>
  );
}

function HeroCard({ member, user }) {
  return (
    <div className="relative w-[320px] sm:w-[380px] mx-auto" style={{ animation: "vipFloat 7s ease-in-out infinite" }}>
      <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(223,23,33,0.55)] border border-white/15"
        style={{ background: "linear-gradient(135deg,#df1721 0%,#df1721 40%,#7c0d11 75%,#1a0202 100%)", aspectRatio: "1.62" }}>
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />
        <div className="vipcard-sheen" />
        <div className="absolute top-0 right-0 bg-white text-black text-[9px] font-black tracking-[0.2em] px-3 py-1 rounded-bl-xl">VIP</div>
        <div className="relative p-5 h-full flex flex-col justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-white/70">Gulf Coast VIP Rewards</p>
            <p className="font-display font-black text-lg text-white leading-tight mt-0.5">{user?.name || "Your Name Here"}</p>
            {member && <p className="text-[10px] text-white/80 mt-0.5">★ {member.member?.tier_name || "VIP Explorer"}</p>}
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="font-display font-black text-2xl text-white leading-none">{member ? member.balance.available.toLocaleString() : "250"}</p>
              <p className="text-[9px] uppercase tracking-widest text-white/70 mt-1">VIP Points {member ? "available" : "welcome bonus"}</p>
            </div>
            <QrCode size={34} className="text-white/85" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MemberStrip({ data }) {
  const m = data.member;
  if (!m) return null;
  const lifetime = m.member.lifetime_earned || 0;
  const cur = m.tier, next = m.next_tier;
  const pct = next ? Math.min(100, Math.round(((lifetime - (cur?.min_lifetime_points || 0)) / (next.min_lifetime_points - (cur?.min_lifetime_points || 0))) * 100)) : 100;
  return (
    <div className="max-w-3xl mx-auto mt-10 bg-white/[0.04] backdrop-blur border border-white/10 rounded-2xl p-5 text-left" data-testid="vip-member-strip">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Available</p>
            <p className="font-display font-black text-2xl text-emerald-300" data-testid="vip-member-available">{m.balance.available.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Pending</p>
            <p className="font-display font-black text-2xl text-zinc-300">{m.balance.pending.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Tier</p>
            <p className="font-display font-black text-2xl" style={{ color: cur?.color || "#fff" }} data-testid="vip-member-tier">{cur?.name || "VIP Explorer"}</p>
          </div>
          {(m.badges || []).includes("five-state-explorer") && (
            <span className="inline-flex items-center gap-1.5 bg-rose-500/15 border border-rose-500/50 text-rose-300 text-xs font-black px-3 py-1.5 rounded-full self-center" data-testid="vip-badge-five-state">
              <Medal size={13} /> Five-State Explorer
            </span>
          )}
        </div>
        <Link to="/my-rewards" className="text-xs font-bold text-rose-400 hover:text-rose-300 flex items-center gap-1" data-testid="vip-member-wallet-link">My Rewards wallet <ChevronRight size={13} /></Link>
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-[11px] text-zinc-500 mb-1.5">
          <span>{lifetime.toLocaleString()} lifetime points</span>
          <span>{next ? `${(next.min_lifetime_points - lifetime).toLocaleString()} to ${next.name}` : "Top tier reached — you're Elite!"}</span>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg,${RED},#ff5560)` }} />
        </div>
      </div>
    </div>
  );
}

function Checklist({ data, reload }) {
  const m = data.member;
  const [city, setCity] = useState(m?.home_city || "");
  const [genres, setGenres] = useState(m?.favorite_genres || []);
  const [bday, setBday] = useState("");
  const [busy, setBusy] = useState("");
  if (!m) return null;
  const actions = m.actions || [];
  const done = actions.filter((a) => a.rewarded).length;

  const save = async (body, key) => {
    setBusy(key);
    try {
      const { data: d } = await api.post("/vip-rewards/preferences", body);
      (d.awarded || []).forEach((a) => toast.success(`+${a.points} VIP Points — ${a.label}!`));
      if (!(d.awarded || []).length) toast.success("Saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const saveBday = async () => {
    setBusy("birthday");
    try {
      await api.post("/loyalty/birthday", { birthday: bday, consent: true });
      toast.success("Birthday saved!");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const toggleGenre = (g) => setGenres((cur) => cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]);
  const controls = {
    profile: (
      <div className="flex gap-2 mt-3">
        <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Your home city (e.g. Gulf Shores)"
          className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-500" data-testid="vip-city-input" />
        <button onClick={() => save({ home_city: city }, "profile")} disabled={!city.trim() || busy === "profile"}
          className="bg-rose-500 hover:bg-rose-600 disabled:opacity-40 text-sm font-bold px-4 rounded-lg" data-testid="vip-city-save">
          {busy === "profile" ? <Loader2 size={14} className="animate-spin" /> : "Save"}
        </button>
      </div>
    ),
    genres: (
      <div className="mt-3">
        <div className="flex flex-wrap gap-1.5">
          {GENRES.map((g) => (
            <button key={g} onClick={() => toggleGenre(g)}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${genres.includes(g) ? "bg-rose-500 border-rose-500 text-white" : "border-white/15 text-zinc-400 hover:border-rose-500/60"}`}
              data-testid={`vip-genre-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{g}</button>
          ))}
        </div>
        <button onClick={() => save({ favorite_genres: genres }, "genres")} disabled={!genres.length || busy === "genres"}
          className="mt-2.5 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 text-sm font-bold px-4 py-1.5 rounded-lg" data-testid="vip-genres-save">
          {busy === "genres" ? <Loader2 size={14} className="animate-spin" /> : `Save ${genres.length || ""} picks`}
        </button>
      </div>
    ),
    notifications: (
      <button onClick={() => save({ rewards_notifications: true }, "notifications")} disabled={busy === "notifications"}
        className="mt-3 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-2" data-testid="vip-notifications-enable">
        <Bell size={14} /> {busy === "notifications" ? "..." : "Turn on rewards notifications"}
      </button>
    ),
    birthday: (
      <div className="flex gap-2 mt-3">
        <input value={bday} onChange={(e) => setBday(e.target.value)} placeholder="MM-DD (e.g. 07-04)"
          className="w-40 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-500" data-testid="vip-birthday-input" />
        <button onClick={saveBday} disabled={!bday.trim() || busy === "birthday"}
          className="bg-rose-500 hover:bg-rose-600 disabled:opacity-40 text-sm font-bold px-4 rounded-lg" data-testid="vip-birthday-save">
          {busy === "birthday" ? <Loader2 size={14} className="animate-spin" /> : "Save"}
        </button>
      </div>
    ),
  };
  const iconFor = { profile: UserRound, genres: Music, notifications: Bell, birthday: Cake };
  return (
    <section className="max-w-5xl mx-auto px-6 mt-20" data-testid="vip-checklist">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-rose-400 font-bold">Easy points</p>
          <h2 className="font-display font-black text-2xl sm:text-3xl mt-1">Your quick-start checklist</h2>
        </div>
        <p className="text-sm text-zinc-500 font-bold">{done}/{actions.length} complete</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {actions.map((a) => {
          const Icon = iconFor[a.key] || Star;
          return (
            <div key={a.key} className={`rounded-2xl border p-5 ${a.rewarded ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.03]"}`} data-testid={`vip-action-${a.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${a.rewarded ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/15 text-rose-400"}`}>
                    {a.rewarded ? <Check size={16} /> : <Icon size={16} />}
                  </span>
                  <div>
                    <p className="font-bold text-sm">{a.label}</p>
                    <p className="text-xs text-zinc-500">{a.hint}</p>
                  </div>
                </div>
                <span className={`text-xs font-black shrink-0 ${a.rewarded ? "text-emerald-300" : "text-rose-400"}`}>{a.rewarded ? "Earned" : `+${a.points}`}</span>
              </div>
              {!a.rewarded && controls[a.key]}
            </div>
          );
        })}
      </div>
    </section>
  );
}

const RULES = [
  "Membership is free and limited to one account per person.",
  "Points have no cash value and cannot be sold.",
  "Points are awarded only after qualifying actions are verified. Purchase points remain pending during the 7-day holding period.",
  "Refunded or cancelled purchases reverse associated points. Cancelled events and bookings do not earn completion bonuses.",
  "Check-in points require verified location, QR, ticket, booking, or event attendance — once per listing every 12 hours.",
  "Duplicate, fraudulent, automated, or misleading activity can result in point removal or suspension.",
  "Reward inventory may be limited. Partner rewards are subject to participating-location rules and availability.",
  "Points expire 12 months after they are earned without qualifying activity.",
  "Alcohol is not offered as a default loyalty reward. Where lawful, an adult beverage benefit may be offered only by the licensed merchant, with age and identity verification.",
  "Availability-based benefits never guarantee admission, seating, inventory, reservations, or access unless the reward has been formally issued.",
  "Gulf Coast VIP may modify point values, eligibility, inventory, or campaigns with appropriate member notice.",
];

export default function VipRewards() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [redeeming, setRedeeming] = useState("");

  const load = () => api.get("/vip-rewards").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [user]);
  useEffect(() => { document.title = "Gulf Coast VIP Rewards — Go More. Discover More. Get Rewarded."; }, []);
  useEffect(() => {
    (data?.member?.just_awarded || []).forEach((a) => toast.success(`+${a.points} VIP Points — ${a.label}!`));
  }, [data]);

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-rose-500" size={32} /></div>;

  const { program, tiers, rewards, earning } = data;
  const m = data.member;
  const byCat = rewards.reduce((acc, r) => {
    const k = r.category || "More Perks";
    if (!acc[k]) acc[k] = [];
    acc[k].push(r);
    return acc;
  }, {});

  const redeem = async (r) => {
    setRedeeming(r.id);
    try {
      const { data: d } = await api.post("/loyalty/redeem", { reward_id: r.id });
      toast.success(`Redeemed: ${r.name}`, { description: `Your one-time code is ${d.code} — it's also saved under My Rewards.`, duration: 12000 });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setRedeeming(""); }
  };

  const shareReferral = async () => {
    try {
      const { data: d } = await api.get(`/loyalty/referral-code?program_id=${program.id}`);
      const url = `${window.location.origin}${d.path}`;
      try { await navigator.clipboard.writeText(url); } catch { /* blocked */ }
      toast.success(`Invite link copied! +${d.referral_points} VIP Points per friend's first purchase.`, { description: url, duration: 10000 });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-black text-white" data-testid="vip-rewards-page">
      <style>{`
        @keyframes vipRise{0%{transform:translateY(0) rotate(0deg);opacity:0}12%{opacity:.75}100%{transform:translateY(-110vh) rotate(340deg);opacity:0}}
        .vip-rise{animation-name:vipRise;animation-timing-function:linear;animation-iteration-count:infinite}
        @keyframes vipFloat{0%,100%{transform:rotate(-3deg) translateY(0)}50%{transform:rotate(-1deg) translateY(-12px)}}
        .vipcard-sheen{position:absolute;inset:-50%;background:linear-gradient(115deg,transparent 40%,rgba(255,255,255,0.22) 50%,transparent 60%);transform:translateX(-40%);animation:vipSheenP 5s ease-in-out infinite;pointer-events:none}
        @keyframes vipSheenP{0%,100%{transform:translateX(-40%)}50%{transform:translateX(40%)}}
      `}</style>

      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden px-6 pt-20 pb-16 text-center" data-testid="vip-hero">
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(60% 50% at 50% 0%, rgba(223,23,33,0.28), transparent 70%), radial-gradient(40% 35% at 85% 60%, rgba(223,23,33,0.14), transparent 70%)` }} />
        <Confetti />
        <div className="relative max-w-4xl mx-auto">
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-rose-400 font-black">Gulf Coast VIP Rewards</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            The Gulf Coast <span className="italic bg-gradient-to-r from-rose-500 via-rose-300 to-white bg-clip-text text-transparent">pays you back</span>
          </h1>
          <p className="inline-flex items-center gap-2 mt-5 text-sm font-black tracking-wide bg-white/[0.06] border border-white/15 rounded-full px-5 py-2" data-testid="vip-tagline">
            <Sparkles size={14} className="text-rose-400" /> Go More. Discover More. Get Rewarded.
          </p>
          <p className="text-zinc-400 max-w-2xl mx-auto mt-5 text-sm sm:text-base">
            Earn VIP Points when you attend featured events, check in at venues and attractions, book activities,
            shop the Marketplace, share reviews and bring friends along — across Florida, Alabama, Mississippi,
            Louisiana and Texas. Redeem for free perks, tickets, upgrades, merch and exclusive experiences.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {!user ? (
              <>
                <Link to="/register" className="bg-rose-500 hover:bg-rose-600 font-black px-7 py-3.5 rounded-full text-sm transition-colors shadow-[0_10px_40px_-8px_rgba(223,23,33,0.7)]" data-testid="vip-join-btn">
                  Join free — get 250 VIP Points
                </Link>
                <a href="#vip-earn" className="border border-white/20 hover:border-rose-500 font-bold px-6 py-3.5 rounded-full text-sm transition-colors" data-testid="vip-how-btn">How it works</a>
              </>
            ) : (
              <span className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-bold text-sm px-5 py-2.5 rounded-full" data-testid="vip-enrolled-badge">
                <Check size={15} /> You're a Gulf Coast VIP Rewards Member
              </span>
            )}
          </div>
          <div className="mt-12">
            {m ? (
              <div className="max-w-md mx-auto text-left" data-testid="vip-hero-member-card">
                <MemberCard program={program} member={m.member} userName={user?.name || ""} onChanged={load} />
              </div>
            ) : (
              <HeroCard member={null} user={null} />
            )}
          </div>
          <MemberStrip data={data} />
        </div>
      </section>

      {/* ---------------- HOW YOU EARN ---------------- */}
      <section id="vip-earn" className="max-w-6xl mx-auto px-6 mt-20" data-testid="vip-earning">
        <p className="text-xs uppercase tracking-[0.25em] text-rose-400 font-bold">Earning VIP Points</p>
        <h2 className="font-display font-black text-2xl sm:text-3xl mt-1 mb-2">Every move you make earns</h2>
        <p className="text-sm text-zinc-500 mb-8 max-w-2xl">{data.redeem_note}. Purchase points are based on the eligible subtotal after discounts and stay pending for 7 days.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {earning.map((grp) => {
            const Icon = ICONS[grp.icon] || Star;
            return (
              <div key={grp.title} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-rose-500/40 transition-colors" data-testid={`vip-earn-${grp.icon}`}>
                <p className="font-display font-bold flex items-center gap-2.5 mb-4"><span className="w-8 h-8 rounded-lg bg-rose-500/15 text-rose-400 flex items-center justify-center"><Icon size={15} /></span>{grp.title}</p>
                <div className="space-y-2.5">
                  {grp.rows.map((r) => (
                    <div key={r.label} className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="text-zinc-400">{r.label}</span>
                      <span className="font-black text-white shrink-0 whitespace-nowrap">{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------- CHECKLIST (members) ---------------- */}
      <Checklist data={data} reload={load} />

      {/* ---------------- TIERS ---------------- */}
      <section className="max-w-6xl mx-auto px-6 mt-20" data-testid="vip-tiers">
        <p className="text-xs uppercase tracking-[0.25em] text-rose-400 font-bold">Membership tiers</p>
        <h2 className="font-display font-black text-2xl sm:text-3xl mt-1 mb-8">Climb the tiers, multiply your points</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((t) => {
            const Icon = TIER_ICON[t.name] || Star;
            const isMine = m && m.tier && m.tier.id === t.id;
            return (
              <div key={t.id} className={`relative rounded-2xl border p-5 flex flex-col ${isMine ? "border-rose-500 bg-rose-500/[0.07]" : "border-white/10 bg-white/[0.03]"}`} data-testid={`vip-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                {isMine && <span className="absolute -top-2.5 left-4 bg-rose-500 text-[10px] font-black px-2.5 py-0.5 rounded-full">YOUR TIER</span>}
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${t.color}22`, color: t.color === "#f4f4f5" ? "#fff" : t.color }}><Icon size={17} /></span>
                  <div>
                    <p className="font-display font-black">{t.name}</p>
                    <p className="text-[11px] text-zinc-500">{t.min_lifetime_points === 0 ? "Free on join" : `${t.min_lifetime_points.toLocaleString()}+ lifetime pts`}</p>
                  </div>
                </div>
                <p className="font-black text-2xl text-rose-400 mb-3">{t.multiplier}× <span className="text-xs text-zinc-500 font-bold">points multiplier</span></p>
                <ul className="space-y-1.5 text-xs text-zinc-400">
                  {(t.benefits || []).slice(0, 6).map((b, i) => <li key={i} className="flex gap-2"><Check size={12} className="text-rose-400 shrink-0 mt-0.5" />{b}</li>)}
                  {(t.benefits || []).length > 6 && <li className="text-zinc-600 pl-5">+{t.benefits.length - 6} more perks</li>}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------- CHALLENGES ---------------- */}
      <section className="max-w-5xl mx-auto px-6 mt-20" data-testid="vip-challenges">
        <p className="text-xs uppercase tracking-[0.25em] text-rose-400 font-bold">Discovery challenges</p>
        <h2 className="font-display font-black text-2xl sm:text-3xl mt-1 mb-8">Bonus points for explorers</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {(m?.challenges || [
            { key: "featured-week", name: "Featured This Week Challenge", description: "Check in at two featured listings in the same week.", target: 2, points: 300, progress: 0, completed: false },
            { key: "monthly-explorer", name: "Monthly Explorer", description: "Complete three verified check-ins in one calendar month.", target: 3, points: 500, progress: 0, completed: false },
            { key: "five-state-explorer", name: "Five-State Explorer", description: "Complete an eligible check-in or purchase in Florida, Alabama, Mississippi, Louisiana and Texas.", target: 5, points: 10000, progress: 0, states: [], completed: false, lifetime: true },
          ]).map((ch) => ch.key === "five-state-explorer" ? (
            <div key={ch.key} className={`sm:col-span-2 relative overflow-hidden rounded-2xl border p-6 ${ch.completed ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/30"}`}
              style={ch.completed ? {} : { background: "linear-gradient(120deg, rgba(223,23,33,0.12), rgba(26,2,2,0.5))" }} data-testid="vip-challenge-five-state-explorer">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${ch.completed ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-400"}`}><Medal size={22} /></span>
                  <div>
                    <p className="font-display font-black text-lg">{ch.name} {ch.completed && <span className="text-emerald-300 text-xs font-black ml-1">BADGE EARNED</span>}</p>
                    <p className="text-sm text-zinc-400">{ch.description}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display font-black text-2xl text-rose-400">+{ch.points.toLocaleString()}</p>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">VIP Points + badge</p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                {["FL", "AL", "MS", "LA", "TX"].map((st) => {
                  const got = (ch.states || []).includes(st);
                  return (
                    <span key={st} className={`inline-flex items-center gap-1.5 text-sm font-black px-4 py-2 rounded-full border transition-colors ${got ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "border-white/15 text-zinc-500"}`} data-testid={`vip-state-${st.toLowerCase()}`}>
                      {got ? <Check size={13} /> : <MapPin size={13} />} {st}
                    </span>
                  );
                })}
                <span className="text-xs font-bold text-zinc-400 ml-auto" data-testid="vip-five-state-progress">{ch.completed ? "All five states explored!" : `${ch.progress}/${ch.target} states`}</span>
              </div>
              <p className="text-[11px] text-zinc-600 mt-3">Lifetime challenge — one-time award. States count from verified check-ins and eligible purchases connected to listings in each state.</p>
            </div>
          ) : (
            <div key={ch.key} className={`rounded-2xl border p-5 ${ch.completed ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-white/[0.03]"}`} data-testid={`vip-challenge-${ch.key}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="font-display font-bold flex items-center gap-2"><Trophy size={16} className={ch.completed ? "text-emerald-300" : "text-rose-400"} /> {ch.name}</p>
                <span className="font-black text-rose-400 text-sm shrink-0">+{ch.points} pts</span>
              </div>
              <p className="text-sm text-zinc-500 mt-1.5">{ch.description}</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-2 flex-1 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.round((ch.progress / ch.target) * 100)}%`, background: ch.completed ? "#34d399" : RED }} />
                </div>
                <span className="text-xs font-bold text-zinc-400">{ch.completed ? "Complete!" : `${ch.progress}/${ch.target}`}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-600 mt-4">Check in with participating QR codes at venues, events, attractions and activities to make challenge progress.</p>
      </section>

      {/* ---------------- REWARDS CATALOG ---------------- */}
      <section className="max-w-6xl mx-auto px-6 mt-20" data-testid="vip-catalog">
        <p className="text-xs uppercase tracking-[0.25em] text-rose-400 font-bold">Free rewards catalog</p>
        <h2 className="font-display font-black text-2xl sm:text-3xl mt-1 mb-2">Spend your VIP Points on the good stuff</h2>
        <p className="text-sm text-zinc-500 mb-8 max-w-2xl">Redeem for a one-time code, then show it at a participating partner — or use points straight at checkout ({data.redeem_note.toLowerCase()}).</p>
        <div className="space-y-10">
          {Object.entries(byCat).map(([cat, items]) => (
            <div key={cat} data-testid={`vip-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Gift size={16} className="text-rose-400" /> {cat}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((r) => {
                  const afford = m && m.balance.available >= r.points_cost;
                  return (
                    <div key={r.id} className="group flex items-center justify-between gap-3 bg-white/[0.03] border border-white/10 hover:border-rose-500/50 rounded-xl px-4 py-3.5 transition-colors" data-testid={`vip-reward-${r.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-bold truncate">{r.name}</p>
                        <p className="text-xs text-rose-400 font-black mt-0.5">{r.points_cost.toLocaleString()} pts</p>
                      </div>
                      {m ? (
                        <button onClick={() => redeem(r)} disabled={!afford || redeeming === r.id}
                          className={`shrink-0 text-xs font-black px-3.5 py-1.5 rounded-full transition-colors ${afford ? "bg-rose-500 hover:bg-rose-600" : "bg-white/5 text-zinc-600 cursor-not-allowed"}`}
                          data-testid={`vip-redeem-${r.id}`}>
                          {redeeming === r.id ? "…" : afford ? "Redeem" : "Locked"}
                        </button>
                      ) : (
                        <Ticket size={16} className="text-zinc-600 shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- REFERRAL ---------------- */}
      <section className="max-w-5xl mx-auto px-6 mt-20" data-testid="vip-referral">
        <div className="relative overflow-hidden rounded-3xl border border-rose-500/30 p-8 sm:p-10 text-center" style={{ background: "linear-gradient(135deg, rgba(223,23,33,0.16), rgba(26,2,2,0.6))" }}>
          <Confetti n={14} />
          <div className="relative">
            <h2 className="font-display font-black text-2xl sm:text-3xl">Bring friends. Stack points.</h2>
            <p className="text-zinc-400 text-sm mt-2 max-w-xl mx-auto">
              Earn <b className="text-white">{program.referral_points || 500} VIP Points</b> when a friend you invite makes their first purchase — and they start with <b className="text-white">{program.referred_bonus_points || 250} bonus points</b> plus the 250-point welcome bonus.
            </p>
            {user ? (
              <button onClick={shareReferral} className="mt-6 inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 font-black px-6 py-3 rounded-full text-sm" data-testid="vip-referral-btn">
                <Share2 size={15} /> Copy my invite link
              </button>
            ) : (
              <Link to="/register" className="mt-6 inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 font-black px-6 py-3 rounded-full text-sm">Join to start referring</Link>
            )}
          </div>
        </div>
      </section>

      {/* ---------------- RULES ---------------- */}
      <section className="max-w-3xl mx-auto px-6 mt-20" data-testid="vip-rules">
        <h2 className="font-display font-black text-xl sm:text-2xl mb-5">Program rules, the short version</h2>
        <div className="space-y-2">
          {RULES.map((r, i) => (
            <div key={i} className="flex gap-3 text-sm text-zinc-400 bg-white/[0.02] border border-white/5 rounded-xl px-4 py-3">
              <span className="text-rose-500 font-black shrink-0">{String(i + 1).padStart(2, "0")}</span>{r}
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="relative overflow-hidden mt-24 py-20 px-6 text-center border-t border-white/10" data-testid="vip-final-cta">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(55% 65% at 50% 100%, rgba(223,23,33,0.3), transparent 70%)" }} />
        <Confetti n={18} />
        <div className="relative">
          <h2 className="font-display font-black text-3xl sm:text-4xl">Go More. Discover More.<br /><span className="text-rose-400">Get Rewarded.</span></h2>
          {!user ? (
            <Link to="/register" className="inline-block mt-8 bg-rose-500 hover:bg-rose-600 font-black px-8 py-4 rounded-full text-sm shadow-[0_10px_40px_-8px_rgba(223,23,33,0.7)]" data-testid="vip-cta-join">
              Join Gulf Coast VIP Rewards — free
            </Link>
          ) : (
            <Link to="/my-rewards" className="inline-block mt-8 bg-rose-500 hover:bg-rose-600 font-black px-8 py-4 rounded-full text-sm" data-testid="vip-cta-wallet">
              Open my rewards wallet
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
